/**
 * Quality checklist display with pass/fail indicators.
 *
 * Supports two formats:
 * - Legacy: hardcoded 7 standard Ehrenberg-Bass gates (backward compatible)
 * - Dynamic: gates from a template snapshot, rendered with role indicators
 */

import { useState } from 'react';

import { Card } from '@/components/ui/Card';
import type { QualityGate, DynamicQualityGate, QualityCheck, TemplateSnapshot } from '@/types';
import type { GateRole } from '@/types/template';

// ============================================================================
// PROPS
// ============================================================================

export interface QualityGateCardProps {
  qualityGate: QualityGate | DynamicQualityGate;
  templateSnapshot?: TemplateSnapshot | null;
}

// ============================================================================
// CHECK ITEM COMPONENT
// ============================================================================

interface CheckItemProps {
  label: string;
  description: string;
  check: QualityCheck;
  role?: GateRole;
}

function CheckItem({ label, description, check, role }: CheckItemProps) {
  const [showEvidence, setShowEvidence] = useState(false);

  return (
    <div className="group">
      <div
        className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
        onClick={() => setShowEvidence(!showEvidence)}
      >
        {/* Pass/Fail Icon */}
        <div className="flex-shrink-0 mt-0.5">
          {check.status === 'PASS' ? (
            <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
              <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          ) : (
            <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center">
              <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
          )}
        </div>

        {/* Label, Description, and Role Badge */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900">{label}</span>
            {role === 'critical' && (
              <span className="text-[10px] font-medium text-red-600 bg-red-50 px-1.5 py-0.5 rounded">
                Critical
              </span>
            )}
            {role === 'standard' && (
              <span className="text-[10px] font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                Standard
              </span>
            )}
          </div>
          {description && (
            <div className="text-xs text-gray-500 mt-0.5">{description}</div>
          )}
        </div>

        {/* Evidence Indicator */}
        <div className="flex-shrink-0">
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${
              showEvidence ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>

      {/* Evidence Tooltip */}
      {showEvidence && (
        <div className="ml-9 mr-3 mb-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="text-xs font-semibold uppercase tracking-wider text-blue-700 mb-1">
            Evidence
          </div>
          <div className="text-sm text-blue-900">{check.evidence}</div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// LEGACY GATE DEFINITIONS (backward compatibility)
// ============================================================================

/** Default checks for the standard (static) QualityGate */
const STANDARD_CHECKS = [
  {
    key: 'productionErrors',
    label: 'Production Errors',
    description: 'No visible technical issues',
  },
  {
    key: 'formatHygiene',
    label: 'Format Hygiene',
    description: 'Correct aspect ratio, safe zones respected',
  },
  {
    key: 'brandTiming',
    label: 'Brand Timing',
    description: 'Brand cue within first 2 seconds',
  },
  {
    key: 'clearClaim',
    label: 'Clear Claim',
    description: 'One identifiable message/claim',
  },
  {
    key: 'productShown',
    label: 'Product Shown',
    description: 'Product appears clearly',
  },
  {
    key: 'ctaPresent',
    label: 'CTA Present',
    description: 'Clear call-to-action visible',
  },
  {
    key: 'brandColorsUsed',
    label: 'Brand Colors',
    description: 'Video uses brand primary colors',
  },
] as const;

// ============================================================================
// HELPERS
// ============================================================================

/** Format a gate ID into a human-readable label (e.g., "gate_hookStrength" -> "Hook Strength") */
function formatGateLabel(gateId: string): string {
  // Strip "gate_" prefix if present
  const name = gateId.startsWith('gate_') ? gateId.slice(5) : gateId;
  // Insert spaces before capitals and capitalize first letter
  return name.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()).trim();
}

/** Check if a quality gate uses the standard (static) format */
function isStandardQualityGate(gate: QualityGate | DynamicQualityGate): gate is QualityGate {
  return 'productionErrors' in gate && 'brandTiming' in gate && 'clearClaim' in gate;
}

/** Resolved check item with all display metadata */
interface ResolvedCheck {
  key: string;
  label: string;
  description: string;
  role?: GateRole;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function QualityGateCard({ qualityGate, templateSnapshot }: QualityGateCardProps) {
  // Build checks array based on format:
  // 1. If templateSnapshot provided -> use template gate metadata (dynamic)
  // 2. If legacy standard gates detected -> use hardcoded 7 gates
  // 3. Fallback: derive labels from gate keys (dynamic without snapshot)
  let checks: ResolvedCheck[];

  if (templateSnapshot) {
    // Dynamic format: build from template snapshot gate metadata
    checks = templateSnapshot.gates
      .map((gate) => ({
        key: gate.gateId,
        label: gate.name,
        description: gate.role === 'critical' ? 'Critical quality gate' : 'Standard quality gate',
        role: gate.role,
      }))
      .filter((c) => {
        const gateRecord = qualityGate as Record<string, QualityCheck>;
        return !!gateRecord[c.key];
      });
  } else if (isStandardQualityGate(qualityGate)) {
    // Legacy format: hardcoded 7 standard Ehrenberg-Bass gates
    checks = [...STANDARD_CHECKS];
  } else {
    // Dynamic without snapshot: derive labels from gate keys
    checks = Object.keys(qualityGate).map((key) => ({
      key,
      label: formatGateLabel(key),
      description: '',
    }));
  }

  // Cast to index-accessible type for uniform access
  const gateRecord = qualityGate as Record<string, QualityCheck>;

  const passCount = checks.filter(
    (check) => gateRecord[check.key]?.status === 'PASS'
  ).length;
  const totalCount = checks.length;

  return (
    <Card className="p-6">
      {/* Header with Pass/Fail Count */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Quality Gate</h3>
          {templateSnapshot && (
            <span className="text-xs text-gray-500">{templateSnapshot.templateName}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`text-sm font-bold ${
              passCount === totalCount ? 'text-green-600' : 'text-orange-600'
            }`}
          >
            {passCount}/{totalCount}
          </span>
          <span className="text-xs text-gray-500">passed</span>
        </div>
      </div>

      {/* Check Items */}
      <div className="space-y-1">
        {checks.map((check) => {
          const checkData = gateRecord[check.key];
          if (!checkData) return null;
          return (
            <CheckItem
              key={check.key}
              label={check.label}
              description={check.description}
              check={checkData}
              role={check.role}
            />
          );
        })}
      </div>

      {/* Overall Status */}
      {passCount === totalCount ? (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
          <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clipRule="evenodd"
            />
          </svg>
          <span className="text-sm font-medium text-green-800">
            All quality checks passed
          </span>
        </div>
      ) : (
        <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg flex items-center gap-2">
          <svg className="w-5 h-5 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <span className="text-sm font-medium text-orange-800">
            {totalCount - passCount} quality {totalCount - passCount === 1 ? 'check' : 'checks'} failed
          </span>
        </div>
      )}
    </Card>
  );
}
